/**
 * IndexedDB: кэш JSON опросов и очередь отправок survey_attempt (офлайн).
 */

const DB_NAME = "spiria-offline-v1";
const DB_VERSION = 2;
const STORE_SURVEYS = "surveySnapshots";
const STORE_OUTBOX = "surveyOutbox";
/** Кэш списка напоминаний по дате (последний успешный ответ my_reminders) */
const STORE_REMINDERS_CACHE = "remindersListCache";

function openDb() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result);
        req.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_SURVEYS)) {
                db.createObjectStore(STORE_SURVEYS, { keyPath: "surveyId" });
            }
            if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
                db.createObjectStore(STORE_OUTBOX, { keyPath: "clientSubmissionId" });
            }
            if (!db.objectStoreNames.contains(STORE_REMINDERS_CACHE)) {
                db.createObjectStore(STORE_REMINDERS_CACHE, { keyPath: "forDate" });
            }
        };
    });
}

/**
 * @param {string} forDate YYYY-MM-DD
 * @param {object[]} reminders
 */
export async function putRemindersListCache(forDate, reminders) {
    if (!forDate || typeof indexedDB === "undefined") return;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_REMINDERS_CACHE, "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        tx.objectStore(STORE_REMINDERS_CACHE).put({
            forDate,
            reminders,
            savedAt: Date.now(),
        });
    });
}

/** @param {string} forDate */
export async function getRemindersListCache(forDate) {
    if (!forDate || typeof indexedDB === "undefined") return null;
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_REMINDERS_CACHE, "readonly");
            const req = tx.objectStore(STORE_REMINDERS_CACHE).get(forDate);
            req.onsuccess = () => resolve(req.result?.reminders ?? null);
            req.onerror = () => reject(req.error);
        });
    } catch {
        return null;
    }
}

export async function putSurveySnapshot(surveyId, data) {
    if (!surveyId || typeof indexedDB === "undefined") return;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_SURVEYS, "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        tx.objectStore(STORE_SURVEYS).put({
            surveyId,
            data,
            savedAt: Date.now(),
        });
    });
}

export async function getSurveySnapshot(surveyId) {
    if (!surveyId || typeof indexedDB === "undefined") return null;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_SURVEYS, "readonly");
        const req = tx.objectStore(STORE_SURVEYS).get(surveyId);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
    });
}

export async function enqueueSurveyOutbox({ clientSubmissionId, body }) {
    if (!clientSubmissionId || typeof indexedDB === "undefined") return;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_OUTBOX, "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        tx.objectStore(STORE_OUTBOX).put({
            clientSubmissionId,
            body,
            createdAt: Date.now(),
        });
    });
}

export async function removeSurveyOutbox(clientSubmissionId) {
    if (!clientSubmissionId || typeof indexedDB === "undefined") return;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_OUTBOX, "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        tx.objectStore(STORE_OUTBOX).delete(clientSubmissionId);
    });
}

export async function listSurveyOutbox() {
    if (typeof indexedDB === "undefined") return [];
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_OUTBOX, "readonly");
        const req = tx.objectStore(STORE_OUTBOX).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });
}

export async function getSurveyOutboxCount() {
    if (typeof indexedDB === "undefined") return 0;
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_OUTBOX, "readonly");
            const req = tx.objectStore(STORE_OUTBOX).count();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    } catch {
        return 0;
    }
}

/**
 * Досылает очередь. postAuthorized — как из hooks/User.
 * @returns {number} сколько записей успешно отправлено и снято с очереди
 */
export async function flushSurveyOutbox(postAuthorized) {
    const items = await listSurveyOutbox();
    if (!items.length) return 0;

    let flushed = 0;
    for (const item of items) {
        try {
            await postAuthorized("/client/survey_attempt", item.body);
            await removeSurveyOutbox(item.clientSubmissionId);
            flushed += 1;
        } catch (e) {
            if (e.response?.status === 401) return flushed;
            if (e.response?.status === 400 || e.response?.status === 422) {
                await removeSurveyOutbox(item.clientSubmissionId);
                continue;
            }
            break;
        }
    }
    return flushed;
}
